import {
  TemplateEditor,
  TemplateEditorRef,
} from '@/components/forms/TemplateEditor';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  getDefaultTemplate,
  getTemplateVariables,
  TemplateVariable,
} from '@/template-mail';
import { EmailTemplate } from '@/types/domain.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

// Validation schema
const emailTemplateSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  subject: z
    .string()
    .min(1, 'Subject is required')
    .max(200, 'Subject must be less than 200 characters'),
  htmlContent: z.string().min(1, 'HTML content is required'),
  isActive: z.boolean(),
  isDefault: z.boolean().optional(),
});

type EmailTemplateFormData = z.infer<typeof emailTemplateSchema>;

interface EmailTemplateEditorProps {
  template?: EmailTemplate | null;
  templateType: 'dsn' | 'bulletins' | 'editions';
  folderUuid: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EmailTemplateFormData & { templateType?: 'dsn' | 'bulletins' | 'editions' }) => void;
  defaultTemplate?: EmailTemplate | null;
  isLoading?: boolean;
}

export const EmailTemplateEditor = ({
  template,
  templateType: initialTemplateType,
  folderUuid: _folderUuid,
  isOpen,
  onClose,
  onSave,
  defaultTemplate: _defaultTemplate,
  isLoading = false,
}: EmailTemplateEditorProps) => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [previewVariables, setPreviewVariables] = useState<
    Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<
    'dsn' | 'bulletins' | 'editions'
  >(initialTemplateType);
  const templateEditorRef = useRef<TemplateEditorRef>(null);


  // Get template variables for the current type
  const templateVariables = getTemplateVariables(selectedTemplateType);
  const defaultTemplateData = getDefaultTemplate(selectedTemplateType);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    reset,
  } = useForm<EmailTemplateFormData>({
    resolver: zodResolver(emailTemplateSchema),
    defaultValues: {
      name: template?.name || '',
      subject: template?.subject || defaultTemplateData?.subject || '',
      htmlContent: template?.htmlContent || defaultTemplateData?.html || '',
      isActive: template?.isActive ?? true,
      isDefault: template?.isDefault ?? false,
    },
  });

  const watchedSubject = watch('subject');
  const watchedHtmlContent = watch('htmlContent');

  // Update selectedTemplateType when initialTemplateType changes
  useEffect(() => {
    setSelectedTemplateType(initialTemplateType);
  }, [initialTemplateType]);

  // Reset form when template changes or template type changes
  useEffect(() => {
    if (template) {
      reset({
        name: template.name,
        subject: template.subject,
        htmlContent: template.htmlContent,
        isActive: template.isActive,
        isDefault: template.isDefault,
      });
    } else if (defaultTemplateData) {
      reset({
        name: `Template ${selectedTemplateType} par défaut`,
        subject: defaultTemplateData.subject,
        htmlContent: defaultTemplateData.html,
        isActive: true,
        isDefault: false,
      });
    }
  }, [template, defaultTemplateData, selectedTemplateType, reset]);

  // Initialize preview variables with sample data
  useEffect(() => {
    const sampleData: Record<string, string> = {};
    templateVariables.forEach((variable: TemplateVariable) => {
      switch (variable.type) {
        case 'text':
          sampleData[variable.key] = `Exemple ${variable.label}`;
          break;
        case 'number':
          sampleData[variable.key] = '123';
          break;
        case 'currency':
          sampleData[variable.key] = '1 234,56 €';
          break;
        case 'date':
          sampleData[variable.key] = new Date().toLocaleDateString('fr-FR');
          break;
        case 'html':
          sampleData[variable.key] =
            '<li>Exemple liste 1</li><li>Exemple liste 2</li>';
          break;
        default:
          sampleData[variable.key] = `Exemple ${variable.label}`;
      }
    });
    setPreviewVariables(sampleData);
  }, [templateVariables]);

  const handleFormSubmit = async (data: EmailTemplateFormData) => {
    setIsSubmitting(true);
    try {
      await onSave({
        ...data,
        templateType: selectedTemplateType,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVariableClick = useCallback((variable: TemplateVariable) => {
    const variableText = `{{${variable.key}}}`;
    // Use the ref to call the insertText method
    if (templateEditorRef.current) {
      templateEditorRef.current.insertText(variableText);
    }
  }, []);

  const handleSubjectVariableClick = useCallback(
    (variable: TemplateVariable) => {
      const variableText = `{{${variable.key}}}`;
      const input = document.querySelector(
        'input[name="subject"]'
      ) as HTMLInputElement;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newContent =
          watchedSubject.substring(0, start) +
          variableText +
          watchedSubject.substring(end);
        setValue('subject', newContent);

        // Focus back to input and set cursor position
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(
            start + variableText.length,
            start + variableText.length
          );
        }, 0);
      }
    },
    [watchedSubject, setValue]
  );

  const renderPreview = () => {
    let previewContent = watchedHtmlContent || '';
    let previewSubject = watchedSubject || '';

    // Replace variables with preview values
    templateVariables.forEach((variable: TemplateVariable) => {
      const placeholder = `{{${variable.key}}}`;
      const value = previewVariables[variable.key] || `{{${variable.key}}}`;
      previewContent = previewContent.replace(
        new RegExp(placeholder, 'g'),
        value
      );
      previewSubject = previewSubject.replace(
        new RegExp(placeholder, 'g'),
        value
      );
    });

    return { content: previewContent, subject: previewSubject };
  };

  const preview = renderPreview();
  const isFormValid = isValid && !isLoading && !isSubmitting;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        template
          ? t('templates.editTemplate', 'Modifier le template')
          : t('templates.createTemplate', 'Créer un template')
      }
      size="xl"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left column - Form */}
          <div className="space-y-6">
            {/* Template Type Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t('templates.templateType', 'Type de template')}
              </label>
              <select
                value={selectedTemplateType}
                onChange={e => {
                  setSelectedTemplateType(
                    e.target.value as 'dsn' | 'bulletins' | 'editions'
                  );
                }}
                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                disabled={!!template} // Disable when editing existing template
              >
                <option value="dsn">{t('templates.dsn', 'DSN')}</option>
                <option value="bulletins">
                  {t('templates.bulletins', 'Bulletins')}
                </option>
                <option value="editions">
                  {t('templates.editions', 'Éditions')}
                </option>
              </select>
            </div>


            {/* Template name */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t('templates.form.name', 'Nom du template')}
              </label>
              <input
                type="text"
                {...register('name')}
                placeholder={t(
                  'templates.form.namePlaceholder',
                  'Nom du template'
                )}
                className={`focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border px-4 py-3 transition-colors focus:ring-2 ${
                  errors.name ? 'border-error-500' : 'border-neutral-300'
                }`}
              />
              {errors.name && (
                <p className="text-error-600 mt-1 text-sm">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Subject */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t('templates.form.subject', "Sujet de l'email")}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  {...register('subject')}
                  placeholder={t(
                    'templates.form.subjectPlaceholder',
                    "Sujet de l'email"
                  )}
                  className={`focus:border-primary-500 focus:ring-primary-100 flex-1 rounded-xl border px-4 py-3 transition-colors focus:ring-2 ${
                    errors.subject ? 'border-error-500' : 'border-neutral-300'
                  }`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-3"
                >
                  {showPreview ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {errors.subject && (
                <p className="text-error-600 mt-1 text-sm">
                  {errors.subject.message}
                </p>
              )}
            </div>

            {/* Variables for subject */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t(
                  'templates.form.subjectVariables',
                  'Variables pour le sujet'
                )}
              </label>
              <div className="flex flex-wrap gap-2">
                {templateVariables.map((variable: TemplateVariable) => (
                  <button
                    key={variable.key}
                    type="button"
                    onClick={() => handleSubjectVariableClick(variable)}
                    className="bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-lg px-3 py-1 text-sm font-medium transition-colors"
                  >
                    {variable.label}
                  </button>
                ))}
              </div>
            </div>

            {/* HTML Content */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t('templates.form.htmlContent', 'Contenu HTML')}
              </label>
              <div className="rounded-xl border border-neutral-300 bg-white p-2">
                <TemplateEditor
                  ref={templateEditorRef}
                  value={watchedHtmlContent || ''}
                  onChange={content => setValue('htmlContent', content)}
                  allowRawHtmlPaste={true}
                />
              </div>
              {errors.htmlContent && (
                <p className="text-error-600 mt-1 text-sm">
                  {errors.htmlContent.message}
                </p>
              )}
            </div>

            {/* Variables for HTML content */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t(
                  'templates.form.htmlVariables',
                  'Variables pour le contenu HTML'
                )}
              </label>
              <div className="flex flex-wrap gap-2">
                {templateVariables.map((variable: TemplateVariable) => (
                  <button
                    key={variable.key}
                    type="button"
                    onClick={() => handleVariableClick(variable)}
                    className="bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-lg px-3 py-1 text-sm font-medium transition-colors"
                  >
                    {variable.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Options */}
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white p-4">
                <div>
                  <p className="text-sm font-medium text-neutral-900">
                    {t('templates.form.isActive', 'Template actif')}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {t(
                      'templates.form.isActiveHelp',
                      "Ce template sera utilisé pour l'envoi d'emails"
                    )}
                  </p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    {...register('isActive')}
                  />
                  <div className="peer-focus:ring-primary-100 peer peer-checked:bg-primary-600 h-6 w-11 rounded-full bg-neutral-200 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all peer-checked:after:translate-x-full" />
                </label>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white p-4">
                <div>
                  <p className="text-sm font-medium text-neutral-900">
                    {t('templates.form.isDefault', 'Template par défaut')}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {t(
                      'templates.form.isDefaultHelp',
                      'Ce template sera utilisé par défaut pour ce type'
                    )}
                  </p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    {...register('isDefault')}
                  />
                  <div className="peer-focus:ring-primary-100 peer peer-checked:bg-primary-600 h-6 w-11 rounded-full bg-neutral-200 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-neutral-300 after:bg-white after:transition-all peer-checked:after:translate-x-full" />
                </label>
              </div>
            </div>
          </div>

          {/* Right column - Preview */}
          {showPreview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-neutral-900">
                  {t('templates.form.preview', 'Aperçu')}
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Preview variables */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-900">
                  {t('templates.form.previewVariables', "Variables d'aperçu")}
                </label>
                {templateVariables.map((variable: TemplateVariable) => (
                  <div key={variable.key} className="flex items-center gap-2">
                    <label className="w-24 truncate text-xs text-neutral-600">
                      {variable.label}:
                    </label>
                    <input
                      type="text"
                      value={previewVariables[variable.key] || ''}
                      onChange={e =>
                        setPreviewVariables(prev => ({
                          ...prev,
                          [variable.key]: e.target.value,
                        }))
                      }
                      className="focus:border-primary-500 focus:ring-primary-100 flex-1 rounded-lg border border-neutral-300 px-2 py-1 text-xs transition-colors focus:ring-1"
                    />
                  </div>
                ))}
              </div>

              {/* Preview content */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-900">
                  {t('templates.form.previewSubject', 'Sujet')}
                </label>
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm">
                  {preview.subject}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-900">
                  {t('templates.form.previewContent', 'Contenu')}
                </label>
                <div className="max-h-96 overflow-auto rounded-lg border border-neutral-200 bg-white p-4">
                  <div
                    className="prose max-w-none text-sm"
                    dangerouslySetInnerHTML={{ __html: preview.content }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form actions */}
        <div className="flex justify-end gap-3 border-t border-neutral-200 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button type="submit" disabled={!isFormValid}>
            {isSubmitting ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {template
                  ? t('templates.form.updating', 'Mise à jour...')
                  : t('templates.form.creating', 'Création...')}
              </>
            ) : template ? (
              t('templates.form.update', 'Mettre à jour')
            ) : (
              t('templates.form.create', 'Créer')
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
